/**
 * «Fillo onboarding» — kalimi nga rekrutimi te procesi.
 *
 * Asgjë nuk niset vetvetiu. Një ofertë e pranuar tregon se personi po vjen; ajo
 * nuk thotë KUR fillon, KUSH e koordinon dhe ME ÇFARË shablloni. Ato tri
 * përgjigje i jep njeriu — dhe pikërisht për këtë ky është dialog me konfirmim
 * dhe jo një efekt anësor i pranimit të ofertës.
 */
import React, { useEffect, useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import onboardingService from '../../services/onboardingService';

export default function StartOnboardingDialog({ hire, open, onClose, onStarted }) {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [templateWarning, setTemplateWarning] = useState('');

  useEffect(() => {
    if (!open) return;
    setError(''); setNotes('');
    // Data e fillimit vjen nga oferta kur ekziston: ajo është data që kandidati
    // ka pranuar, dhe rishkrimi i saj me dorë është vetëm mundësi për gabim.
    setStartDate(hire?.startDate ? String(hire.startDate).slice(0, 10) : '');
    setTemplateWarning('');
    onboardingService().templates().then((d) => {
      const all = Array.isArray(d) ? d : [];
      const list = all.filter((t) => t.isActive);
      setTemplates(list);

      // Pozita mund të propozojë një shabllon. Nëse ai është çaktivizuar ose
      // fshirë, NUK përdoret në heshtje: HR-ja e mëson dhe zgjedh vetë.
      const wanted = hire?.defaultTemplateId ? String(hire.defaultTemplateId) : '';
      if (wanted) {
        const active = list.find((t) => String(t.id) === wanted);
        if (active) { setTemplateId(wanted); return; }
        const known = all.find((t) => String(t.id) === wanted);
        setTemplateWarning(known
          ? `Shablloni i pozitës «${known.name}» është joaktiv — zgjidh një tjetër.`
          : 'Shablloni i parazgjedhur i pozitës nuk ekziston më — zgjidh një tjetër.');
        setTemplateId('');
        return;
      }
      const def = list.find((t) => t.isDefault) || list[0];
      setTemplateId(def ? String(def.id) : '');
    });
  }, [open, hire]);

  const start = async () => {
    setBusy(true); setError('');
    const r = await onboardingService().start({
      applicationId: hire?.applicationId,
      workerId: hire?.workerId || undefined,
      templateId: Number(templateId),
      startDate,
      notes,
    });
    setBusy(false);
    if (r?.error) { setError(r.error); return; }
    if (onStarted) onStarted(r);
    onClose();
  };

  const chosen = templates.find((t) => String(t.id) === String(templateId));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Fillo onboarding
        <Typography variant="body2" color="text.secondary">{hire?.name || ''}</Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {templateWarning && <Alert severity="warning">{templateWarning}</Alert>}
          {templates.length === 0 && (
            <Alert severity="warning">
              S'ka shabllon aktiv. Hap faqen e Onboarding-ut dhe krijo shabllonin standard së pari.
            </Alert>
          )}
          <TextField select label="Shablloni" fullWidth required value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}>
            {templates.map((t) => (
              <MenuItem key={t.id} value={String(t.id)}>
                {t.name}{t.isDefault ? ' (parazgjedhur)' : ''} · {t.taskCount} detyra
              </MenuItem>
            ))}
          </TextField>
          <TextField type="date" label="Dita e parë e punës" fullWidth required
            InputLabelProps={{ shrink: true }} value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            helperText="Afatet e detyrave llogariten nga kjo datë" />
          <TextField label="Shënim (opsional)" fullWidth multiline minRows={2}
            value={notes} onChange={(e) => setNotes(e.target.value)} />
          {chosen && startDate && (
            <Alert severity="info">
              Do të krijohen <strong>{chosen.taskCount} detyra</strong> nga shablloni «{chosen.name}».
              Ndryshimet e mëvonshme të shabllonit nuk e prekin këtë proces.
            </Alert>
          )}
          {!hire?.workerId && (
            <Typography variant="caption" color="text.secondary">
              Punonjësi ende s'është te Stafi. Procesi nis gjithsesi; lidhja bëhet më vonë me dorë —
              asnjë punonjës nuk krijohet vetvetiu.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Anulo</Button>
        <Button variant="contained" disabled={!templateId || !startDate || busy} onClick={start}>
          Fillo
        </Button>
      </DialogActions>
    </Dialog>
  );
}
